import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useAvailableTrailers } from "../../../hooks/useVehicleQuery";
import { ReactComponent as SearchIcon } from "../../../assets/svg/components/search.svg";
import { ReactComponent as LinkIcon } from "../../../assets/svg/components/archive.svg";//link.svg
import { ReactComponent as UnlinkIcon } from "../../../assets/svg/components/trash.svg";//unlink.svg
import LoadingAnimation from "../../loading/LoadingAnimation";
import "../../../assets/scss/components/vehicle/TrailerSelector.scss";

const TrailerSelector = ({
  currentTrailerId = null,
  onTrailerSelect,
  excludeVehicleId = null,
  theme,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    trailers,
    pagination,
    isLoading,
    filters,
    updateFilters,
    goToNextPage,
    goToPrevPage
  } = useAvailableTrailers({
    excludeVehicleId,
    includeAssociated: true
  });

  // Efecto para búsqueda con debounce
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      updateFilters({ search: searchTerm });
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, updateFilters]);

  const handleTrailerSelect = (trailer) => {
    onTrailerSelect(trailer);
    setIsOpen(false);
  };

  const currentTrailer = trailers.find(t => t.id === currentTrailerId);

  return (
    <div className={`GM__${theme}-trailer-selector`}>
      <div className={`GM__${theme}-selector-header`}>
        <label className={`GM__${theme}-selector-label`}>
          Trailer asociado:
        </label>
        <button
            className={`GM__${theme}-selector-toggle`}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(!isOpen);
            }}
            disabled={disabled}
            type="button"
            >
          {currentTrailer ? (
            <div className={`GM__${theme}-current-trailer`}>
              <LinkIcon className={`GM__${theme}-status-icon linked`} />
              <span>{currentTrailer.plate}</span>
              <span className={`GM__${theme}-trailer-model`}>
                {currentTrailer.model} ({currentTrailer.year})
              </span>
            </div>
          ) : (
            <div className={`GM__${theme}-no-trailer`}>
              <UnlinkIcon className={`GM__${theme}-status-icon unlinked`} />
              <span>Sin trailer asociado</span>
            </div>
          )}
        </button>
      </div>

      {isOpen && (
        <div className={`GM__${theme}-selector-dropdown`}>
          <div className={`GM__${theme}-search-container`}>
            <SearchIcon className={`GM__${theme}-search-icon`} />
            <input
              type="text"
              placeholder="Buscar trailer por placa, modelo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`GM__${theme}-search-input`}
            />
          </div>

          <div className={`GM__${theme}-trailer-list`}>
            {isLoading ? (
              <div className={`GM__${theme}-loading-container`}>
                <LoadingAnimation />
              </div>
            ) : trailers.length === 0 ? (
              <div className={`GM__${theme}-no-results`}>
                No se encontraron trailers
              </div>
            ) : (
              <>
                {/* Opción para desasociar */}
                {currentTrailerId && (
                  <div
                    className={`GM__${theme}-trailer-item remove-association`}
                    onClick={() => handleTrailerSelect(null)}
                  >
                    <UnlinkIcon className={`GM__${theme}-item-icon`} />
                    <div className={`GM__${theme}-item-info`}>
                      <span className={`GM__${theme}-item-title`}>
                        Desasociar trailer actual
                      </span>
                    </div>
                  </div>
                )}

                {trailers.map((trailer) => (
                  <div
                    key={trailer.id}
                    className={`GM__${theme}-trailer-item ${
                      trailer.is_associated ? 'associated' : 'available'
                    } ${trailer.id === currentTrailerId ? 'current' : ''}`}
                    onClick={() => handleTrailerSelect(trailer)}
                  >
                    <div className={`GM__${theme}-item-status`}>
                      {trailer.is_associated ? (
                        <LinkIcon className={`GM__${theme}-status-icon linked`} />
                      ) : (
                        <UnlinkIcon className={`GM__${theme}-status-icon available`} />
                      )}
                    </div>

                    <div className={`GM__${theme}-item-info`}>
                      <div className={`GM__${theme}-item-header`}>
                        <span className={`GM__${theme}-item-plate`}>
                          {trailer.plate}
                        </span>
                        <span className={`GM__${theme}-item-type`}>
                          {trailer.vehicle_type_name}
                        </span>
                      </div>

                      <div className={`GM__${theme}-item-details`}>
                        <span className={`GM__${theme}-item-model`}>
                          {trailer.brand_name} {trailer.model} ({trailer.year})
                        </span>
                        {trailer.capacity && (
                          <span className={`GM__${theme}-item-capacity`}>
                            {trailer.capacity} ton
                          </span>
                        )}
                      </div>

                      {trailer.is_associated && (
                        <div className={`GM__${theme}-association-info`}>
                          <span className={`GM__${theme}-associated-text`}>
                            Asociado a: {trailer.associated_to_vehicle_plate}
                          </span>
                        </div>
                      )}

                      {trailer.owner_name && (
                        <div className={`GM__${theme}-owner-info`}>
                          Propietario: {trailer.owner_name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Paginación */}
                {pagination.lastPage > 1 && (
                  <div className={`GM__${theme}-pagination`}>
                    <button
                      onClick={goToPrevPage}
                      disabled={pagination.currentPage === 1}
                      className={`GM__${theme}-pagination-button`}
                    >
                      Anterior
                    </button>
                    <span className={`GM__${theme}-pagination-info`}>
                      Página {pagination.currentPage} de {pagination.lastPage}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`GM__${theme}-pagination-button`}
                    >
                      Siguiente
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

TrailerSelector.propTypes = {
  currentTrailerId: PropTypes.string,
  onTrailerSelect: PropTypes.func.isRequired,
  excludeVehicleId: PropTypes.string,
  theme: PropTypes.oneOf(["dark", "light"]).isRequired,
  disabled: PropTypes.bool
};

export default TrailerSelector;